import { useEffect, useRef, useState } from 'react';
import { getInquiry } from '@/api/inquiries';
import type { Inquiry } from '@/types/chat/chat';
import { applyLocalFileRemovals } from '../removeFileFromInquiry';

interface UseInquiryOptions {
  inquiryId: string | undefined;
  /** Any change reloads the inquiry. */
  reloadData: unknown;
  reloadInquiry: unknown;
  removedFileIdsFor: (inquiryId: string) => Set<string>;
  /**
   * Called once per selection of an inquiry, after its first successful
   * load (a Retry after a failure included), with the stored inquiry.
   */
  onOpened: (inquiryId: string, inquiry: Inquiry) => void;
}

/**
 * The selected inquiry, GET /chat/inquiry/{id}, reloaded on every change of
 * the inquiry or of a reload trigger. Files removed in this session stay
 * removed from every payload.
 */
export const useInquiry = ({
  inquiryId,
  reloadData,
  reloadInquiry,
  removedFileIdsFor,
  onOpened,
}: UseInquiryOptions) => {
  const [inquiry, setInquiry] = useState<any>();
  // Only the window's very first load marks the send button as busy.
  const [firstLoad, setFirstLoad] = useState(false);
  /** A load of the current inquiry is on its way (its totals may change). */
  const [loading, setLoading] = useState(false);
  /** The selected inquiry could not be loaded (Retry reloads it). */
  const [failed, setFailed] = useState(false);
  /**
   * The inquiry whose stored state (mode) has been applied since it was
   * selected. Until it equals the selected id, the data on screen may be
   * an older visit's or another inquiry's.
   */
  const [openedId, setOpenedId] = useState<string | null>(null);
  const loadSeq = useRef(0);
  /**
   * Every request (load or refresh) in the order it started, and the newest
   * one whose answer is on screen: an older answer never replaces a newer
   * one, whichever arrives last.
   */
  const requestSeq = useRef(0);
  const appliedSeq = useRef(0);
  const firstLoadPendingRef = useRef(true);
  const latestInquiryIdRef = useRef<string | null>(null);
  /** The inquiry whose stored state onOpened has applied since it was selected. */
  const openedRef = useRef<string | null>(null);

  /** Apply the stored state once per selection, on its first successful answer. */
  const markOpened = (id: string, stored: Inquiry) => {
    if (openedRef.current === id) return;
    openedRef.current = id;
    onOpened(id, stored);
    setOpenedId(id);
  };

  const show = (id: string, data: Inquiry, ticket: number) => {
    if (ticket <= appliedSeq.current) return;
    appliedSeq.current = ticket;
    setInquiry(applyLocalFileRemovals(data, removedFileIdsFor(id)));
  };

  useEffect(() => {
    // Selecting another inquiry: its stored state has to be applied again
    // once its first load succeeds (also after a failed load and Retry).
    if (latestInquiryIdRef.current !== (inquiryId ?? null)) {
      openedRef.current = null;
      setOpenedId(null);
    }
    latestInquiryIdRef.current = inquiryId ?? null;
    const load = async () => {
      if (!inquiryId) return;
      const isFirstLoad = firstLoadPendingRef.current;
      if (isFirstLoad) setFirstLoad(true);

      const inquiryIdAtFetch = inquiryId;
      const seq = (loadSeq.current += 1);
      const ticket = (requestSeq.current += 1);
      setLoading(true);
      setFailed(false);
      const response = await getInquiry(inquiryIdAtFetch);

      if (isFirstLoad) setFirstLoad(false);
      firstLoadPendingRef.current = false;
      // Only the latest load counts: an older one (another inquiry selected
      // meanwhile, or a reload started after it) must not apply its data or
      // its stored mode.
      if (
        seq !== loadSeq.current ||
        latestInquiryIdRef.current !== inquiryIdAtFetch
      ) {
        return;
      }
      setLoading(false);
      if (!response?.success) {
        // A refresh that started after this load and succeeded is newer.
        if (appliedSeq.current < ticket) setFailed(true);
        return;
      }
      markOpened(inquiryIdAtFetch, response.data);
      show(inquiryIdAtFetch, response.data, ticket);
    };
    load();
  }, [inquiryId, reloadData, reloadInquiry]);

  /** The inquiry on screen now, as of its latest selection. */
  const currentInquiryId = () => latestInquiryIdRef.current;

  /** Whether `id` is still the inquiry on screen. */
  const isCurrentInquiry = (id: string) => currentInquiryId() === id;

  /**
   * Reload after the set of finished files changed; true when applied.
   * `stillWanted` lets the caller drop an answer it no longer needs. A
   * success also ends a failed state (and applies the stored state if the
   * failed load was the first), like a successful Retry.
   */
  const refreshInquiry = async (
    inquiryIdAtFetch: string,
    stillWanted: () => boolean,
  ) => {
    // A load started meanwhile (after a change) answers with newer data;
    // one started before may answer later with older data (show() skips it).
    const seq = loadSeq.current;
    const ticket = (requestSeq.current += 1);
    const response = await getInquiry(inquiryIdAtFetch);
    if (!stillWanted()) return false;
    if (seq !== loadSeq.current) return false;
    if (!isCurrentInquiry(inquiryIdAtFetch)) return false;
    if (!response?.success) return false;
    if (ticket <= appliedSeq.current) return false;
    markOpened(inquiryIdAtFetch, response.data);
    setFailed(false);
    show(inquiryIdAtFetch, response.data, ticket);
    return true;
  };

  return {
    inquiry,
    setInquiry,
    firstLoad,
    loading,
    failed,
    openedId,
    currentInquiryId,
    isCurrentInquiry,
    refreshInquiry,
  };
};
